import {
  ImplantReportInputs,
  ImplantReportStatus,
  ImplantReportType,
  TUploadFileObject,
} from 'common/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useGetProcedureReportPreview,
  useSendImplantReportEmailMutation,
} from 'lib/apollo/hooks'
import {
  ImplantReportInputValue,
  ImplantReportSenderModalLogicProps,
} from '../ImplantReportSender.types'
import { z } from 'zod'
import { enqueueSnackbar } from 'notistack'
import { fileToBase64WithThumbnail } from 'lib/utils/fileToBase64'
import { pdfToBase64Image } from 'common/utils'
import {
  ORGANIZATIONS,
  useOrganizationSettings,
} from 'lib/context/OrganizationSettingsContext'
import { useUser } from 'app/User'

export const useImplantReportSenderModalLogic = ({
  assetIds,
  surgeryId,
  implantReportStatus,
  implantReportInputs,
  setImplantReportInputs,
  unformatEnumValue,
  setErrorMessage,
  formatEnumValue,
  setInputModalOpen,
}: ImplantReportSenderModalLogicProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Hooks
  const [sendImplantReport, sendImplantReportMutation] =
    useSendImplantReportEmailMutation()

  const [previewImplantReport, { loading: previewImplantReportLoading }] =
    useGetProcedureReportPreview()

  const { physiciansTitle } = useOrganizationSettings()

  const { user } = useUser()
  const isCommaSeparatedInput =
    user?.orgId === ORGANIZATIONS.medgeo ||
    user?.orgId === ORGANIZATIONS.ridgewood ||
    user?.orgId === ORGANIZATIONS.bch

  const isBch =
    user?.orgId === ORGANIZATIONS.medgeo || user?.orgId === ORGANIZATIONS.bch

  // States
  const [reportAlreadySentModal, setReportAlreadySentModal] =
    useState<boolean>(false)
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false)
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>('')
  const [extractionValues, setExtractionValues] = useState<
    Array<string | number>
  >([''])
  const [implantNumbers, setImplantNumbers] = useState<string[]>([''])
  const [attachments, setAttachments] = useState<TUploadFileObject[]>([])

  // Constants
  const reportAlreadySent = (option: keyof typeof ImplantReportType) => {
    const enumType = unformatEnumValue(option)
    return (implantReportStatus as ImplantReportStatus[]).some(
      (status) => status.type === enumType && status.isSent
    )
  }

  // Handlers
  const handleChangeFormData = <Key extends keyof ImplantReportInputs>(
    reportType: Key,
    value: ImplantReportInputValue<Key>
  ) => {
    let processedValue = value
    if (value instanceof Date) {
      const year = value.getFullYear()
      const month = String(value.getMonth() + 1).padStart(2, '0')
      const day = String(value.getDate()).padStart(2, '0')
      const dateString = `${year}-${month}-${day}T00:00:00.000Z`
      processedValue = dateString as ImplantReportInputValue<Key>
    }

    setValidationErrors(null)
    setImplantReportInputs((prevState) => ({
      ...prevState,
      reportInputs: {
        ...prevState.reportInputs,
        [reportType]: processedValue,
      },
    }))
  }

  const handleAddExtractionInput = () => {
    setExtractionValues((prev) => [...prev, ''])
  }

  const handleAddImplantNumber = () => {
    setImplantNumbers((prev) => [...prev, ''])
  }

  const handleRemoveExtractionInput = (index: number) => {
    if (extractionValues.length === 1) return

    setExtractionValues((prev) => {
      const newValues = prev.filter((_, i) => i !== index)

      setImplantReportInputs((prevInputs) => ({
        ...prevInputs,
        reportInputs: {
          ...prevInputs.reportInputs,
          extractionValues: newValues,
        },
      }))

      return newValues
    })
  }

  const handleRemoveImplantNumber = (index: number) => {
    if (implantNumbers.length === 1) return

    setImplantNumbers((prev) => {
      const newValues = prev.filter((_, i) => i !== index)

      setImplantReportInputs((prevInputs) => ({
        ...prevInputs,
        reportInputs: {
          ...prevInputs.reportInputs,
          implantNumbers: newValues,
        },
      }))

      return newValues
    })
  }

  const handleChangeExtractionInput = (index: number, value: string) => {
    setValidationErrors(null)
    if (isCommaSeparatedInput) {
      const values = value
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v !== '')
      setExtractionValues([value])
      handleChangeFormData('extractionValues', values)
    } else {
      const newValues = [...extractionValues]
      newValues[index] = value.slice(0, 4)
      setExtractionValues(newValues)
      handleChangeFormData('extractionValues', newValues)
    }
  }

  const handleChangeImplantNumber = (index: number, value: string) => {
    setValidationErrors(null)
    if (isCommaSeparatedInput) {
      const values = value
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v !== '')
      setImplantNumbers([value])
      handleChangeFormData('implantNumbers', values)
    } else {
      const newValues = [...implantNumbers]
      newValues[index] = value.slice(0, 4)
      setImplantNumbers(newValues)
      handleChangeFormData('implantNumbers', newValues)
    }
  }

  const implantReportSchema = z.object({
    extractionValues: z
      .array(z.string().min(1, 'Extraction site should not be empty'))
      .optional()
      .superRefine((extractionValues, context) => {
        if (
          implantReportInputs.reportType === 'EXTRACTION' ||
          implantReportInputs.reportType === 'EXTRACTION_AND_BONE_GRAFT' ||
          implantReportInputs.reportType === 'IMPLANT_AND_EXTRACTION'
        ) {
          if (!extractionValues || extractionValues.length === 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Extraction site should not be empty',
            })
          }
        }
      }),
    implantNumbers: z
      .array(z.string().min(1, 'Implant number should not be empty'))
      .optional()
      .superRefine((implantNumbers, context) => {
        if (implantReportInputs.reportType === 'TORQUE_TEST') {
          if (!implantNumbers || implantNumbers.length === 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Implant number should not be empty',
            })
          }
        }
      }),
    implantPartsOption: z
      .string()
      .optional()
      .superRefine((implantPartsOption, context) => {
        if (implantReportInputs.reportType === 'TORQUE_TEST') {
          if (!implantPartsOption || implantPartsOption.length === 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Implant parts option should not be empty',
            })
          }
        }
      }),
    procedure: z
      .string()
      .optional()
      .superRefine((procedure, context) => {
        if (implantReportInputs.reportType === 'SURGERY') {
          if (!procedure || procedure.length === 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Procedure should not be empty',
            })
          }
        }
      }),
    additionalComments: z
      .string()
      .optional()
      .superRefine((additionalComments, context) => {
        if (implantReportInputs.reportType === 'OTHER') {
          if (!additionalComments || additionalComments.length === 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Additional comments should not be empty',
            })
          }
        }
      }),
    consultationReason: z
      .string()
      .optional()
      .superRefine((consultationReason, context) => {
        if (implantReportInputs.reportType === 'CONSULTATION') {
          if (!consultationReason || consultationReason.length === 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Consultation reason should not be empty',
            })
          }
        }
      }),
    finalTreatmentPlan: z
      .string()
      .optional()
      .superRefine((finalTreatmentPlan, context) => {
        if (implantReportInputs.reportType === 'CONSULTATION') {
          if (
            (!finalTreatmentPlan || finalTreatmentPlan.length === 0) &&
            !isBch
          ) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Final treatment plan should not be empty',
            })
          }
        }
      }),
    scheduledProcedureDate: z.string().optional(),
    nextApptDate: z
      .string()
      .optional()
      .superRefine((nextApptDate, context) => {
        if (
          implantReportInputs.reportType === 'FIRST_STAGE' ||
          implantReportInputs.reportType === 'SINGLE_STAGE'
        ) {
          if (!nextApptDate) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Next appointment date should not be empty',
            })
          }
        }
      }),
    nextApptInMonths: z
      .number()
      .optional()
      .superRefine((nextApptInMonths, context) => {
        if (
          implantReportInputs.reportType === 'FIRST_STAGE' ||
          implantReportInputs.reportType === 'SINGLE_STAGE'
        ) {
          if (!nextApptInMonths && nextApptInMonths !== 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Next appointment date should not be empty',
            })
          }
          if (nextApptInMonths === 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Next appointment date should not be zero',
            })
          }
        }
      }),
    secondStageAbutmentType: z
      .string()
      .optional()
      .superRefine((secondStageAbutmentType, context) => {
        if (implantReportInputs.reportType === 'FIRST_STAGE') {
          if (
            !secondStageAbutmentType ||
            secondStageAbutmentType.length === 0
          ) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Second stage abutment type should not be empty',
            })
          }
        }
      }),
    torqueTestDate: z
      .string()
      .optional()
      .superRefine((torqueTestDate, context) => {
        if (
          (implantReportInputs.reportType === 'IMPLANT' ||
            implantReportInputs.reportType === 'IMPLANT_AND_EXTRACTION') &&
          !isBch
        ) {
          if (!torqueTestDate) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Torque test date is required',
            })
          }
        }
      }),
  })

  type TValidationError = {
    extractionValues?: string[] | undefined
    implantNumbers?: string[] | undefined
    additionalComments?: string[] | undefined
    consultationReason?: string[] | undefined
    finalTreatmentPlan?: string[] | undefined
    scheduledProcedureDate?: string[] | undefined
    nextApptDate?: string[] | undefined
    nextApptInMonths?: string[] | undefined
    secondStageAbutmentType?: string[] | undefined
    torqueTestDate?: string[] | undefined
    implantPartsOption?: string[] | undefined
    procedure?: string[] | undefined
  }

  const [validationErrors, setValidationErrors] =
    useState<TValidationError | null>(null)

  const validateAndSubmit = () => {
    const validationResult = implantReportSchema.safeParse(
      implantReportInputs.reportInputs
    )

    if (!validationResult.success) {
      const errors = validationResult.error.formErrors.fieldErrors

      const mappedErrors = {
        ...errors,
        extractionValues: extractionValues.map((value, index) => {
          const v = value as string
          return v.trim() === '' && errors.extractionValues
            ? errors.extractionValues[0]
            : undefined
        }),
        implantNumbers: implantNumbers.map((value, index) => {
          const v = value as string
          return v.trim() === '' && errors.implantNumbers
            ? errors.implantNumbers[0]
            : undefined
        }),
      }

      setValidationErrors(mappedErrors as TValidationError)
      return
    }

    const reportType =
      implantReportInputs.reportType as keyof typeof ImplantReportType
    if (reportAlreadySent(reportType)) {
      setReportAlreadySentModal(true)
      return
    }
    submitReport()
  }

  // solves async state update on submitOverride flag issue
  useEffect(() => {
    if (implantReportInputs.submitOverride) {
      submitReport()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [implantReportInputs.submitOverride])

  const handleOnClose = () => {
    setInputModalOpen(false)
    setImplantReportInputs({
      surgeryId,
      reportType: undefined,
      referringPhysicianEmail: implantReportInputs.referringPhysicianEmail,
      referringPhysicianLastName:
        implantReportInputs.referringPhysicianLastName,
      reportInputs: {
        assetIds: assetIds,
      },
      submitOverride: false,
    })
    setExtractionValues([''])
    setImplantNumbers([''])
  }

  const submitReport = async () => {
    try {
      if (
        attachments.reduce((acc, curr) => acc + curr.size, 0) >=
        4 * 1024 * 1024
      ) {
        enqueueSnackbar('Attachments size exceeds 4MB', {
          variant: 'error',
        })
        setReportAlreadySentModal(false)
        return
      }

      const attachmentsToSend = attachments.map((attachment) => {
        const { size, thumbnail, ...rest } = attachment
        return rest
      })

      const processTextFieldsWithNewLine = (text: string | undefined) => {
        if (!text) return text
        return `<br>${text.replace(/\n/g, '<br>')}`
      }

      const processTextFields = (text: string | undefined) => {
        if (!text) return text
        return text.replace(/\n/g, '<br>')
      }

      const reportVariables = {
        ...implantReportInputs,
        reportInputs: {
          ...implantReportInputs.reportInputs,
          extractionValues: implantReportInputs.reportInputs.extractionValues
            ?.map((value) => {
              if (typeof value === 'string' && value.includes(',')) {
                return value
                  .split(',')
                  .map((site) => site.trim())
                  .filter((site) => site !== '')
              }
              return value
            })
            .flat()
            .filter((item) => item !== ''),
          implantNumbers:
            implantReportInputs.reportInputs.implantNumbers?.filter(
              (item) => item !== ''
            ),
          implantPartsOption:
            implantReportInputs.reportInputs.implantPartsOption,
          attachments: attachmentsToSend,
          consultationReason: processTextFields(
            implantReportInputs.reportInputs.consultationReason
          ),
          finalTreatmentPlan: processTextFieldsWithNewLine(
            implantReportInputs.reportInputs.finalTreatmentPlan
          ),
          additionalComments:
            implantReportInputs.reportInputs.additionalComments,
          procedure: processTextFieldsWithNewLine(
            implantReportInputs.reportInputs.procedure
          ),
        },
      }

      const result = await sendImplantReport({
        variables: {
          implantReportEmailInput: reportVariables,
        },
      })

      if (result.data?.sendImplantReportEmail?.success) {
        enqueueSnackbar(
          `${formatEnumValue(
            implantReportInputs?.[
              'reportType'
            ] as keyof typeof ImplantReportType
          )} Report Sent Successfully`,
          {
            variant: 'success',
          }
        )
        setInputModalOpen(false)
        setReportAlreadySentModal(false)
        setImplantReportInputs((prevState) => ({
          ...prevState,
          reportType: undefined,
          submitOverride: false,
        }))
      } else {
        enqueueSnackbar(
          `Failed to send report. Products are required for ${formatEnumValue(
            implantReportInputs?.[
              'reportType'
            ] as keyof typeof ImplantReportType
          )} report.`,
          {
            variant: 'error',
          }
        )
        setReportAlreadySentModal(false)
        setErrorMessage(result.data?.sendImplantReportEmail?.message)
      }
    } catch (error) {
      enqueueSnackbar('Failed to send report. Please try again later.', {
        variant: 'error',
      })
    }
  }

  const handleAttach = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }, [])

  const handleAttachementFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files || null

    if (!selectedFiles || selectedFiles.length === 0) {
      console.error('No file selected.')
      return
    }

    try {
      const newAttachments = await Promise.all(
        Array.from(selectedFiles).map(async (file: File) => {
          const { base64, thumbnail } = await fileToBase64WithThumbnail(file)
          if (base64 && thumbnail) {
            return {
              filename: file.name,
              mimetype: file.type,
              encoding: 'base64',
              content: base64,
              size: file.size,
              thumbnail:
                file.name.split('.').pop()?.toLocaleLowerCase() === 'pdf'
                  ? await pdfToBase64Image(base64)
                  : thumbnail,
            }
          }
        })
      )

      setAttachments((prev) => [
        ...prev,
        ...newAttachments.filter(
          (attachment): attachment is TUploadFileObject =>
            attachment !== undefined
        ),
      ])
    } catch (error) {
      console.error('Error processing files:', error)
    }
  }

  const handleRemoveAttachment = (index: number) => () => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const validateAndPreview = () => {
    const validationResult = implantReportSchema.safeParse(
      implantReportInputs.reportInputs
    )

    if (!validationResult.success) {
      const errors = validationResult.error.formErrors.fieldErrors

      const mappedErrors = {
        ...errors,
        extractionValues: extractionValues.map((value, index) => {
          const v = value as string
          return v.trim() === '' && errors.extractionValues
            ? errors.extractionValues[0]
            : undefined
        }),
        implantNumbers: implantNumbers.map((value, index) => {
          const v = value as string
          return v.trim() === '' && errors.implantNumbers
            ? errors.implantNumbers[0]
            : undefined
        }),
      }

      setValidationErrors(mappedErrors as TValidationError)
      return
    }

    previewReport()
  }

  const previewReport = async () => {
    try {
      if (
        attachments.reduce((acc, curr) => acc + curr.size, 0) >=
        4 * 1024 * 1024
      ) {
        enqueueSnackbar('Attachments size exceeds 4MB', {
          variant: 'error',
        })
        return
      }

      const attachmentsToSend = attachments.map((attachment) => {
        const { size, thumbnail, ...rest } = attachment
        return rest
      })

      const processTextFieldsWithNewLine = (text: string | undefined) => {
        if (!text) return text
        return `<br>${text.replace(/\n/g, '<br>')}`
      }

      const processTextFields = (text: string | undefined) => {
        if (!text) return text
        return text.replace(/\n/g, '<br>')
      }

      const reportVariables = {
        ...implantReportInputs,
        reportInputs: {
          ...implantReportInputs.reportInputs,
          extractionValues: implantReportInputs.reportInputs.extractionValues
            ?.map((value) => {
              if (typeof value === 'string' && value.includes(',')) {
                return value
                  .split(',')
                  .map((site) => site.trim())
                  .filter((site) => site !== '')
              }
              return value
            })
            .flat()
            .filter((item) => item !== ''),
          implantNumbers:
            implantReportInputs.reportInputs.implantNumbers?.filter(
              (item) => item !== ''
            ),
          implantPartsOption:
            implantReportInputs.reportInputs.implantPartsOption,
          attachments: attachmentsToSend,
          consultationReason: processTextFields(
            implantReportInputs.reportInputs.consultationReason
          ),
          finalTreatmentPlan: processTextFieldsWithNewLine(
            implantReportInputs.reportInputs.finalTreatmentPlan
          ),
          additionalComments:
            implantReportInputs.reportInputs.additionalComments,
          procedure: processTextFieldsWithNewLine(
            implantReportInputs.reportInputs.procedure
          ),
        },
      }

      const previewInput = {
        surgeryId,
        referringPhysicianLastName:
          implantReportInputs.referringPhysicianLastName || '',
        referringPhysicianEmail:
          implantReportInputs.referringPhysicianEmail || '',
        reportType: implantReportInputs.reportType!,
        reportInputs: reportVariables.reportInputs,
      }

      const result = await previewImplantReport({
        variables: {
          input: previewInput,
        },
      })

      if (result.data?.previewImplantReport?.success) {
        if (result.data.previewImplantReport.html) {
          setPreviewPdfUrl(result.data.previewImplantReport.html)
          setPreviewModalOpen(true)
        }
      } else {
        enqueueSnackbar(
          `Failed to send report. Products are required for ${formatEnumValue(
            implantReportInputs?.[
              'reportType'
            ] as keyof typeof ImplantReportType
          )} report.`,
          {
            variant: 'error',
          }
        )
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate preview. Please try again later.', {
        variant: 'error',
      })
      console.error('Preview error:', error)
    }
  }

  const handleClosePreview = () => {
    setPreviewModalOpen(false)
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl)
      setPreviewPdfUrl('')
    }
  }

  return {
    attachments,
    fileInputRef,
    physiciansTitle,
    validationErrors,
    extractionValues,
    implantNumbers,
    reportAlreadySentModal,
    sendImplantReportMutation,
    previewImplantReportLoading,
    previewModalOpen,
    previewPdfUrl,
    handleAttach,
    submitReport,
    handleOnClose,
    validateAndSubmit,
    validateAndPreview,
    handleClosePreview,
    handleChangeFormData,
    handleRemoveAttachment,
    handleAddExtractionInput,
    handleAddImplantNumber,
    setReportAlreadySentModal,
    handleChangeExtractionInput,
    handleChangeImplantNumber,
    handleRemoveExtractionInput,
    handleRemoveImplantNumber,
    handleAttachementFileChange,
    isCommaSeparatedInput,
    isBch,
  }
}
